import { useEffect, useId, useState } from 'react';

import { Textarea } from '@/components/ui/textarea';

/** Instagram's limit is the tightest of the three platforms that take alt text. */
export const ALT_TEXT_SOFT_LIMIT = 1000;

interface AltTextFieldProps {
  /** One-based position, shown when the post has more than one image. */
  slideNumber?: number;
  value: string;
  onCommit: (next: string) => void;
  disabled?: boolean;
}

/**
 * Alt text for the selected image. Saved when the field loses focus, and sent to Instagram,
 * Facebook and LinkedIn with the post (TikTok has no alt text field).
 */
export function AltTextField({ slideNumber, value, onCommit, disabled }: AltTextFieldProps) {
  const id = useId();
  const [draft, setDraft] = useState(value);

  useEffect(() => {
    setDraft(value);
  }, [value]);

  const over = draft.length > ALT_TEXT_SOFT_LIMIT;

  return (
    <div className="space-y-1" data-testid="alt-text-field">
      <label htmlFor={id} className="text-caption font-medium text-foreground">
        {slideNumber ? `Alt text for image ${slideNumber}` : 'Alt text'}
      </label>
      <Textarea
        id={id}
        value={draft}
        rows={2}
        disabled={disabled}
        placeholder="Describe the image for people who cannot see it"
        onChange={(event) => setDraft(event.target.value)}
        onBlur={() => {
          const next = draft.trim();
          if (next !== value.trim()) onCommit(next);
        }}
        aria-describedby={`${id}-hint`}
        aria-invalid={over || undefined}
      />
      <p
        id={`${id}-hint`}
        className={over ? 'text-detail text-destructive' : 'text-detail text-muted-foreground'}
      >
        {over
          ? `Instagram keeps the first ${ALT_TEXT_SOFT_LIMIT.toLocaleString()} characters.`
          : 'Read aloud by screen readers. Sent to Instagram, Facebook and LinkedIn.'}
      </p>
    </div>
  );
}
